import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../config/api';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const Checkout = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_street: '',
    shipping_city: '',
    shipping_country: '',
    billing_street: '',
    billing_city: '',
    billing_country: '',
    payment_method: 'paynow'
  });

  if (!cartItems || cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare order data - include cart items for guest users
      const orderData = {
        ...formData,
        items: (cartItems || []).map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      };

      const response = await api.post('/orders/create', orderData);
      
      // If Paynow payment URL is returned, redirect to Paynow
      if (response.data.payment && response.data.payment.redirectUrl) {
        // Don't clear cart yet - wait for payment confirmation
        window.location.href = response.data.payment.redirectUrl;
      } else {
        // For non-Paynow payments (if any), proceed normally
        await clearCart();
        navigate(`/order-confirmation/${response.data.order.order_number}`);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create order. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Checkout - NieHairLabel</title>
      </Helmet>
      <div className="checkout-page">
        <div className="container">
          <h1>Checkout</h1>
          <div className="checkout-content">
            <form onSubmit={handleSubmit} className="checkout-form">
              <section>
                <h2>Shipping Information</h2>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="shipping_street"
                    value={formData.shipping_street}
                    onChange={handleChange}
                    placeholder="e.g. 123 Warren Park"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="shipping_city"
                    value={formData.shipping_city}
                    onChange={handleChange}
                    placeholder="e.g. Harare"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="shipping_country"
                    value={formData.shipping_country}
                    onChange={handleChange}
                    placeholder="e.g. Zimbabwe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.billing_street === '' && formData.billing_city === '' && formData.billing_country === ''}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            billing_street: '',
                            billing_city: '',
                            billing_country: ''
                          });
                        }
                      }}
                    />
                    Use shipping address for billing
                  </label>
                </div>
                {(!formData.billing_street && !formData.billing_city && !formData.billing_country) ? null : (
                  <>
                    <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Billing Address</h3>
                    <div className="form-group">
                      <label>Billing Street Address</label>
                      <input
                        type="text"
                        name="billing_street"
                        value={formData.billing_street}
                        onChange={handleChange}
                        placeholder="e.g. 123 Warren Park"
                      />
                    </div>
                    <div className="form-group">
                      <label>Billing City</label>
                      <input
                        type="text"
                        name="billing_city"
                        value={formData.billing_city}
                        onChange={handleChange}
                        placeholder="e.g. Harare"
                      />
                    </div>
                    <div className="form-group">
                      <label>Billing Country</label>
                      <input
                        type="text"
                        name="billing_country"
                        value={formData.billing_country}
                        onChange={handleChange}
                        placeholder="e.g. Zimbabwe"
                      />
                    </div>
                  </>
                )}
              </section>

              <section>
                <h2>Payment Method</h2>
                <div className="form-group">
                  <label>
                    <input
                      type="radio"
                      name="payment_method"
                      value="paynow"
                      checked={formData.payment_method === 'paynow'}
                      onChange={handleChange}
                    />
                    Paynow (Credit Card, Mobile Money, Zimswitch)
                  </label>
                </div>
                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                  You will be redirected to Paynow to complete your payment securely.
                </p>
              </section>

              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={loading}
              >
                {loading ? 'Processing...' : `Place Order - $${parseFloat(cartTotal || 0).toFixed(2)}`}
              </button>
            </form>

            <div className="order-summary">
              <h2>Order Summary</h2>
              <div className="summary-items">
                {cartItems && cartItems.map(item => (
                  <div key={item.id} className="summary-item">
                    <span>{item.name} × {item.quantity}</span>
                    <span>${(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="summary-total">
                <span>Total</span>
                <span>${parseFloat(cartTotal || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;

